import {
  areaAt,
  hasLowerLevel,
  isMapId,
  levelAt,
  mapInfo,
  onRadar,
  parseGetpos,
  radarToWorld,
  teleportCommand,
  worldToRadar,
  type Level,
  type MapId,
} from "@69ly/maps";
import {
  CLICKS,
  formatClock,
  MAX_CLIP_BYTES,
  GRENADES,
  MAX_MEDIA_PER_STUFF,
  MEDIA_CAPTIONS,
  MOVEMENTS,
  parseTimestamp,
  parseYouTube,
  StuffInput,
  type Area,
  type Click,
  type Grenade,
  type Importance,
  type Landing,
  type MediaCaption,
  type Movement,
  type Side,
  type Stuff,
} from "@69ly/shared";
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation, useNavigate, useParams } from "react-router";
import { AreaField, PlacesSummary } from "../components/AreaField.tsx";
import { ClipPlayer } from "../components/ClipPlayer.tsx";
import { ClipCodecNote } from "../components/ClipCodecNote.tsx";
import { DropZone, IMAGE_ACCEPT, VIDEO_ACCEPT } from "../components/DropZone.tsx";
import { GrenadeGlyph } from "../components/GrenadeGlyph.tsx";
import { ImportanceField } from "../components/Importance.tsx";
import { Segmented } from "../components/Segmented.tsx";
import { OnMap, Radar, ThrowLine } from "../components/Radar.tsx";
import { Banner, ErrorState, Loading } from "../components/Status.tsx";
import { parseWindows, windowDrafts, WindowsEditor, type WindowDraft } from "../components/WindowsEditor.tsx";
import { useCurrentTeam } from "../lib/current-team.tsx";
import { useCallouts } from "../lib/callouts.ts";
import { errorText } from "../lib/errors.ts";
import { useLeave } from "../lib/leave.ts";
import { uploadClip, uploadPicture, useRemoveClip, useRemovePicture, useSaveStuff, useStuff } from "../lib/stuff-queries.ts";
import { MarkerIcon } from "../components/MarkerIcon.tsx";

export { parseClock } from "../components/WindowsEditor.tsx";
export { Segmented } from "../components/Segmented.tsx";

interface PictureDraft {
  file: File;
  caption: MediaCaption;
}

const fixed = (n: number) => n.toFixed(1).replace(/\.0$/, "");

/** A new stuff started from a throw seen in a demo: spot, view, landing and side filled. */
export interface StuffDraft {
  throw: { x: number; y: number; z: number; pitch: number; yaw: number };
  landing: Landing;
  grenade: Grenade;
  side: Side;
  movement?: Movement;
  jump?: boolean;
  notes?: string;
}

function StuffForm({ map, stuff, draft }: { map: MapId; stuff?: Stuff; draft?: StuffDraft }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const leave = useLeave();
  const { scope } = useCurrentTeam();
  const save = useSaveStuff(stuff?.id);
  const removePicture = useRemovePicture(stuff?.id ?? "");
  const removeClip = useRemoveClip(stuff?.id ?? "");
  const [clipFile, setClipFile] = useState<File | null>(null);
  const [clipProgress, setClipProgress] = useState<number | null>(null);
  const info = mapInfo(map);
  const callouts = useCallouts();

  const [throwText, setThrowText] = useState(stuff ? teleportCommand(stuff.throw) : draft ? teleportCommand(draft.throw) : "");
  const [landing, setLanding] = useState<Landing | null>(stuff?.landing ?? draft?.landing ?? null);
  const [landingText, setLandingText] = useState("");
  const [level, setLevel] = useState<Level>(stuff ? levelAt(map, stuff.landing.z ?? stuff.throw.z) : draft ? levelAt(map, draft.landing.z ?? draft.throw.z) : "upper");
  const [grenade, setGrenade] = useState<Grenade>(stuff?.grenade ?? draft?.grenade ?? "smoke");
  const [side, setSide] = useState<Side>(stuff?.side ?? draft?.side ?? "t");
  // The area follows the landing unless the player picks one; a saved area that differs from the map's was picked.
  const [areaChoice, setAreaChoice] = useState<Area | null>(() =>
    stuff &&
    stuff.area !== callouts.area(map, stuff.landing.x, stuff.landing.y, stuff.landing.z ?? stuff.throw.z) &&
    stuff.area !== areaAt(map, stuff.landing.x, stuff.landing.y, stuff.landing.z ?? stuff.throw.z)
      ? stuff.area
      : null,
  );
  const [title, setTitle] = useState(stuff?.title ?? "");
  const [movement, setMovement] = useState<Movement>(stuff?.technique.movement ?? draft?.movement ?? "standing");
  const [jump, setJump] = useState(stuff?.technique.jump ?? draft?.jump ?? false);
  const [click, setClick] = useState<Click>(stuff?.technique.click ?? "left");
  const [windows, setWindows] = useState<WindowDraft[]>(windowDrafts(stuff?.windows));
  const [youtubeUrl, setYoutubeUrl] = useState(
    stuff?.youtube ? `https://youtu.be/${stuff.youtube.id}${stuff.youtube.start != null ? `?t=${stuff.youtube.start}` : ""}` : "",
  );
  const [clipEnd, setClipEnd] = useState(stuff?.youtube?.end != null ? formatClock(stuff.youtube.end) : "");
  const [notes, setNotes] = useState(stuff?.notes ?? draft?.notes ?? "");
  const [importance, setImportance] = useState<Importance>(stuff?.importance ?? "useful");
  const [pictures, setPictures] = useState<PictureDraft[]>([]);
  const [error, setError] = useState<string | null>(null);
  // Upload errors survive the switch from the new-stuff form to the edit form.
  const location = useLocation();
  const [uploadErrors, setUploadErrors] = useState<string[]>(
    () => (location.state as { uploadErrors?: string[] } | null)?.uploadErrors ?? [],
  );
  const [busy, setBusy] = useState(false);

  const parsed = throwText.trim() ? parseGetpos(throwText) : null;
  const throwSpot = parsed && parsed.pitch !== undefined && parsed.yaw !== undefined ? { ...parsed, pitch: parsed.pitch, yaw: parsed.yaw } : null;
  const throwOffMap = throwSpot ? !onRadar(worldToRadar(map, throwSpot.x, throwSpot.y)) : false;
  const youtube = youtubeUrl.trim() ? parseYouTube(youtubeUrl) : null;
  const existing = stuff?.media ?? [];
  const pictureRoom = MAX_MEDIA_PER_STUFF - existing.length - pictures.length;

  const autoArea: Area | null = landing
    ? callouts.area(map, landing.x, landing.y, landing.z ?? throwSpot?.z ?? null)
    : throwSpot
      ? callouts.area(map, throwSpot.x, throwSpot.y, throwSpot.z)
      : null;
  const area: Area = areaChoice ?? autoArea ?? "other";
  const throwPlace = throwSpot ? callouts.at(map, throwSpot.x, throwSpot.y, throwSpot.z) : null;
  const landingPlace = landing ? callouts.at(map, landing.x, landing.y, landing.z ?? throwSpot?.z ?? null) : null;
  const titleIdea = landingPlace ? `${landingPlace} ${t(`grenade.${grenade}`).toLowerCase()}` : null;

  const pickLanding = (u: number, v: number) => {
    const w = radarToWorld(map, { u, v });
    // A click gives no height. On the lower floor of a two-level map, store a
    // height just under the split so the stuff shows on that floor.
    const lower = mapInfo(map).lowerBelowZ;
    setLanding({ x: w.x, y: w.y, z: level === "lower" && lower !== undefined ? lower - 100 : null });
  };

  // Pictures fill the stand, aim and landing slots first, in the order they come.
  const addPictures = (picked: File[]) => {
    const files = picked.slice(0, pictureRoom);
    const order: MediaCaption[] = ["stand", "aim", "landing"];
    const used = new Set([...existing.map((m) => m.caption), ...pictures.map((p) => p.caption)]);
    setPictures([
      ...pictures,
      ...files.map((file) => {
        const caption = order.find((c) => !used.has(c)) ?? "other";
        used.add(caption);
        return { file, caption };
      }),
    ]);
  };
  const pickClip = (f: File | null) => {
    if (f && f.size > MAX_CLIP_BYTES) return setError(t("form.clipTooLarge", { mb: MAX_CLIP_BYTES / 1024 / 1024 }));
    setError(null);
    setClipFile(f);
  };
  const refused = (names: string[]) => setError(t("drop.refused", { names: names.join(", ") }));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!throwSpot) return setError(t("form.getposBad"));
    if (!landing) return setError(t("form.landingNone"));
    if (youtubeUrl.trim() && !youtube) return setError(t("form.youtubeBad"));
    const parsedWindows = parseWindows(windows);
    const end = clipEnd.trim() ? parseTimestamp(clipEnd) : null;
    const input = StuffInput.safeParse({
      map,
      grenade,
      side,
      area,
      title,
      throw: throwSpot,
      landing,
      technique: { movement, jump, click },
      notes,
      importance,
      youtube: youtube ? { id: youtube.id, start: youtube.start, end } : null,
      windows: parsedWindows,
    });
    if (!input.success) return setError(input.error.issues[0]?.message ?? t("errors.invalid_input"));

    setBusy(true);
    try {
      const saved = await save.mutateAsync(stuff ? input.data : { ...input.data, scope });
      const failed: string[] = [];
      for (const p of pictures) {
        try {
          await uploadPicture(saved.id, p.file, p.caption);
        } catch (err) {
          failed.push(t("form.uploadFailed", { name: p.file.name, reason: errorText(t, err) }));
        }
      }
      if (clipFile) {
        setClipProgress(0);
        try {
          await uploadClip(saved.id, clipFile, setClipProgress);
        } catch (err) {
          const reason = err instanceof Error && "code" in err && err.code === "too_large" ? t("form.clipTooLarge", { mb: MAX_CLIP_BYTES / 1024 / 1024 }) : errorText(t, err);
          failed.push(t("form.uploadFailed", { name: clipFile.name, reason }));
        } finally {
          setClipProgress(null);
          setClipFile(null);
        }
      }
      if (failed.length) {
        setUploadErrors(failed);
        setPictures([]);
        navigate(`/stuffs/${saved.id}/edit`, { replace: true, state: { uploadErrors: failed } });
      } else if (stuff) leave.back(`/stuffs/${saved.id}`);
      else leave.to(`/stuffs/${saved.id}`);
    } catch (err) {
      setError(errorText(t, err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="page stuff-form" onSubmit={submit} noValidate>
      <div className="page-head">
        <h1 className="page-title">{stuff ? t("form.editTitle", { title: stuff.title }) : t("form.newTitle", { map: info.name })}</h1>
        <div className="row">
          <button type="button" className="btn btn-quiet" onClick={() => leave.back(stuff ? `/stuffs/${stuff.id}` : `/library/${map}`)}>
            {t("form.cancel")}
          </button>
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {clipProgress !== null ? t("form.uploadingClip", { percent: Math.round(clipProgress * 100) }) : busy ? t("form.saving") : t("form.save")}
          </button>
        </div>
      </div>
      {error && <Banner tone="warning">{error}</Banner>}
      {stuff?.usedIn && stuff.usedIn.length > 0 && (
        <Banner tone="info">
          {t("usedIn.editWarning", { count: stuff.usedIn.length, names: stuff.usedIn.map((u) => u.title).join(", ") })}
        </Banner>
      )}
      {uploadErrors.map((m) => (
        <Banner key={m} tone="warning">
          {m}
        </Banner>
      ))}

      <div className="columns">
        <div className="column-main form-main">
          <section className="card">
            <h2 className="section-title">1 · {t("form.stepThrow")}</h2>
            <p className="muted">{t("form.stepThrowHelp")}</p>
            <label className="field">
              <span>{t("form.getpos")}</span>
              <textarea
                className="mono"
                rows={2}
                value={throwText}
                onChange={(e) => setThrowText(e.target.value)}
                placeholder={t("form.getposPlaceholder")}
                spellCheck={false}
              />
            </label>
            {throwText.trim() !== "" &&
              (!parsed ? (
                <p className="field-error">{t("form.getposBad")}</p>
              ) : !throwSpot ? (
                <p className="field-error">{t("form.getposNoAngles")}</p>
              ) : throwOffMap ? (
                <p className="field-error">{t("form.offMap", { map: info.name })}</p>
              ) : (
                <p className="ok-text small">
                  {t("form.getposOk", {
                    x: fixed(throwSpot.x),
                    y: fixed(throwSpot.y),
                    z: fixed(throwSpot.z),
                    pitch: fixed(throwSpot.pitch),
                    yaw: fixed(throwSpot.yaw),
                  })}
                  {throwPlace && ` · ${throwPlace}`}
                </p>
              ))}
          </section>

          <section className="card">
            <h2 className="section-title">2 · {t("form.stepLanding")}</h2>
            <p className="muted">{t("form.stepLandingHelp")}</p>
            {hasLowerLevel(map) && (
              <div className="segmented" role="group" aria-label={t("level.label")}>
                {(["upper", "lower"] as const).map((l) => (
                  <button key={l} type="button" className="seg" aria-pressed={level === l} onClick={() => setLevel(l)}>
                    {t(`level.${l}`)}
                  </button>
                ))}
              </div>
            )}
            <div className="pick-radar">
              <Radar map={map} mapName={info.name} level={level} onPick={(p) => pickLanding(p.u, p.v)}>
                {throwSpot && landing && <ThrowLine map={map} from={throwSpot} to={landing} side={side} />}
                {throwSpot && levelAt(map, throwSpot.z) === level && (
                  <OnMap map={map} x={throwSpot.x} y={throwSpot.y} className="throw-spot">
                    <span className={`throw-marker side-${side}`} title={t("mapView.throwFrom")}>
                      <MarkerIcon kind="launch" grenade={grenade} side={side} />
                    </span>
                  </OnMap>
                )}
                {landing && levelAt(map, landing.z) === level && (
                  <OnMap map={map} x={landing.x} y={landing.y}>
                    <span className={`marker is-static is-selected side-${side}`} title={t("mapView.landsHere")}>
                      <MarkerIcon kind="impact" grenade={grenade} side={side} selected />
                    </span>
                  </OnMap>
                )}
              </Radar>
            </div>
            <p className={landing ? "ok-text small" : "muted small"} aria-live="polite">
              {landing ? `${t("form.landingSet")}: ${fixed(landing.x)}, ${fixed(landing.y)}${landingPlace ? ` · ${landingPlace}` : ""}` : t("form.landingNone")}
            </p>
            <label className="field">
              <span>{t("form.landingGetpos")}</span>
              <input
                className="mono"
                value={landingText}
                spellCheck={false}
                onChange={(e) => {
                  setLandingText(e.target.value);
                  const p = parseGetpos(e.target.value);
                  if (p) {
                    setLanding({ x: p.x, y: p.y, z: p.z });
                    setLevel(levelAt(map, p.z));
                  }
                }}
                placeholder="setpos -980 -1280 -167"
              />
            </label>
          </section>

          <section className="card">
            <h2 className="section-title">3 · {t("form.stepDetails")}</h2>
            <Segmented
              label={t("form.grenade")}
              options={GRENADES}
              value={grenade}
              onChange={setGrenade}
              render={(g) => (
                <>
                  <GrenadeGlyph grenade={g} size={12} /> {t(`grenadeShort.${g}`)}
                </>
              )}
            />
            <div className="field-row">
              <Segmented label={t("form.side")} options={["t", "ct"] as const} value={side} onChange={setSide} render={(s) => t(`sideShort.${s}`)} />
              <AreaField
                label={t("form.area")}
                auto={autoArea}
                choice={areaChoice}
                onChoice={setAreaChoice}
                autoHelp={landingPlace && autoArea ? t("areaField.stuffFromCallout", { place: landingPlace, area: t(`area.${autoArea}`) }) : t("areaField.stuffHelp")}
                noAutoHelp={t("areaField.stuffNone")}
                detail={<PlacesSummary from={throwPlace} to={landingPlace} />}
              />
            </div>
            <ImportanceField value={importance} onChange={setImportance} />
            <label className="field">
              <span>{t("form.titleLabel")}</span>
              <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={titleIdea ?? t("form.titlePlaceholder")} maxLength={60} />
            </label>
            {!title.trim() && titleIdea && (
              <button type="button" className="link-button small title-idea" onClick={() => setTitle(titleIdea)}>
                {t("form.useTitle", { title: titleIdea })}
              </button>
            )}
            <h3 className="eyebrow">{t("form.technique")}</h3>
            <div className="field-row">
              <Segmented label={t("form.movement")} options={MOVEMENTS} value={movement} onChange={setMovement} render={(m) => t(`movement.${m}`)} />
              <Segmented label={t("form.click")} options={CLICKS} value={click} onChange={setClick} render={(c) => t(`click.${c}`)} />
            </div>
            <label className="check">
              <input type="checkbox" checked={jump} onChange={(e) => setJump(e.target.checked)} />
              {t("form.jump")}
            </label>
          </section>
        </div>

        <aside className="column-side form-side">
          <section className="card">
            <h2 className="section-title">{t("form.windows")}</h2>
            <p className="muted small">{t("form.windowsHelp")}</p>
            <WindowsEditor windows={windows} onChange={setWindows} />
          </section>

          <section className="card">
            <h2 className="section-title">{t("form.media")}</h2>
            <label className="field">
              <span>{t("form.youtube")}</span>
              <input value={youtubeUrl} onChange={(e) => setYoutubeUrl(e.target.value)} placeholder="https://youtu.be/…?t=42" />
            </label>
            {youtubeUrl.trim() && !youtube && <p className="field-error">{t("form.youtubeBad")}</p>}
            {youtube && (
              <label className="field">
                <span>{t("form.clipEnd")}</span>
                <input className="mono" value={clipEnd} onChange={(e) => setClipEnd(e.target.value)} placeholder="1:05" />
              </label>
            )}
            <h3 className="eyebrow">{t("form.clip")}</h3>
            <p className="muted small">{t("form.clipHelp", { mb: MAX_CLIP_BYTES / 1024 / 1024 })}</p>
            {stuff?.clip && !clipFile && (
              <div className="clip-current">
                <ClipPlayer src={stuff.clip.url} title={t("form.clip")}/>
                <button type="button" className="btn btn-quiet btn-small btn-danger" disabled={removeClip.isPending} onClick={() => removeClip.mutate()}>
                  {t("form.removeClip")}
                </button>
              </div>
            )}
            {clipFile && (
              <p className="small">
                <span className="file-name">{clipFile.name}</span> · {(clipFile.size / 1024 / 1024).toFixed(1)} MB{" "}
                <button type="button" className="link-button" onClick={() => setClipFile(null)}>
                  {t("form.removePicture")}
                </button>
              </p>
            )}
            <ClipCodecNote file={clipFile} />
            {clipProgress !== null && <progress className="clip-progress" value={clipProgress} max={1} aria-label={t("form.uploadingClip", { percent: Math.round(clipProgress * 100) })} />}
            <DropZone accept={VIDEO_ACCEPT} hint={t("drop.video")} onFiles={([f]) => pickClip(f)} onRefused={refused}>
              <label className="btn btn-secondary btn-small file-button">
                {stuff?.clip || clipFile ? t("form.replaceClip") : t("form.addClip")}
                <input
                  type="file"
                  accept={VIDEO_ACCEPT.join(",")}
                  onChange={(e) => {
                    const f = e.target.files?.[0] ?? null;
                    e.target.value = "";
                    pickClip(f);
                  }}
                />
              </label>
            </DropZone>
            <h3 className="eyebrow">{t("form.pictures")}</h3>
            <p className="muted small">{t("form.picturesHelp")}</p>
            <ul className="picture-list">
              {existing.map((m) => (
                <li key={m.id}>
                  <img src={m.url} alt="" />
                  <span>{t(`detail.${m.caption}`)}</span>
                  <button type="button" className="btn btn-quiet btn-small" onClick={() => removePicture.mutate(m.id)}>
                    {t("form.removePicture")}
                  </button>
                </li>
              ))}
              {pictures.map((p, i) => (
                <li key={`${p.file.name}-${i}`}>
                  <span className="file-name">{p.file.name}</span>
                  <label>
                    <span className="visually-hidden">{t("form.caption")}</span>
                    <select
                      value={p.caption}
                      onChange={(e) => setPictures(pictures.map((x, j) => (j === i ? { ...x, caption: e.target.value as MediaCaption } : x)))}
                    >
                      {MEDIA_CAPTIONS.map((c) => (
                        <option key={c} value={c}>
                          {t(`detail.${c}`)}
                        </option>
                      ))}
                    </select>
                  </label>
                  <button type="button" className="btn btn-quiet btn-small" onClick={() => setPictures(pictures.filter((_, j) => j !== i))}>
                    {t("form.removePicture")}
                  </button>
                </li>
              ))}
            </ul>
            {pictureRoom > 0 && (
              <DropZone accept={IMAGE_ACCEPT} multiple hint={t("drop.pictures")} onFiles={addPictures} onRefused={refused}>
                <label className="btn btn-secondary btn-small file-button">
                  {t("form.addPicture")}
                  <input
                    type="file"
                    accept={IMAGE_ACCEPT.join(",")}
                    multiple
                    onChange={(e) => {
                      addPictures(Array.from(e.target.files ?? []));
                      e.target.value = "";
                    }}
                  />
                </label>
              </DropZone>
            )}
          </section>

          <section className="card">
            <label className="field">
              <span>{t("form.notes")}</span>
              <textarea rows={4} value={notes} maxLength={1000} onChange={(e) => setNotes(e.target.value)} />
            </label>
          </section>
        </aside>
      </div>
    </form>
  );
}

export function NewStuffPage() {
  const { map } = useParams();
  const draft = (useLocation().state as { draft?: StuffDraft } | null)?.draft;
  if (!isMapId(map)) return <Navigate to="/library" replace />;
  return <StuffForm map={map} draft={draft} />;
}

export function EditStuffPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const stuff = useStuff(id);
  if (stuff.isPending) return <Loading />;
  if (stuff.error) return <ErrorState message={errorText(t, stuff.error)} onRetry={() => void stuff.refetch()} />;
  if (!isMapId(stuff.data.map) || !stuff.data.canEdit) return <Navigate to={`/stuffs/${stuff.data.id}`} replace />;
  return <StuffForm map={stuff.data.map} stuff={stuff.data} />;
}
